"use client";

import { useMemo } from "react";
import { isAddress } from "viem";
import { useAccount, useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";

/**
 * Outcome of validating a candidate "referrer" address -- who invited the
 * buyer, distinct from the "parent" (where they sit in the binary tree).
 *
 * `Users._validateFirstPurchaseReferrer` only checks existence-in-genealogy:
 *   - `ReferrerNotPlaced` → `referrer != 0 && !_isInGenealogy(referrer)`
 * There is deliberately NO capacity check here (unlike the parent field, see
 * {@link useParentFieldValidation}): a referrer is not accepting a new direct
 * child, they are only being credited with the referral cut, so a referrer
 * whose own tree has both legs full is still perfectly valid. Reusing the
 * parent validator for this field would incorrectly block that referrer.
 *
 * "In genealogy" is `exists && (id == 1 || depth > 0)`, NOT `exists` alone —
 * see the note on ParentFieldStatus for why.
 */
export type ReferrerFieldStatus =
  | "idle" // nothing typed yet — show no feedback
  | "invalid" // not a well-formed 0x address
  | "self" // the buyer's own wallet
  | "checking" // chain read in flight
  | "not-member" // not in the genealogy: never placed in the tree
  | "unreachable" // RPC failed — we genuinely do not know
  | "valid";

/** The tree root is the one placed node legitimately at depth 0. */
const ROOT_USER_ID = 1n;

/**
 * Status → key under the `checkout.referrer` message namespace.
 */
export const REFERRER_MESSAGE_KEY: Record<ReferrerFieldStatus, string> = {
  idle: "optionalFirstPurchase",
  invalid: "invalid",
  self: "self",
  checking: "checking",
  "not-member": "notMember",
  unreachable: "unreachable",
  valid: "valid",
};

export interface UseReferrerFieldValidationReturn {
  status: ReferrerFieldStatus;
  /** True only when the chain confirms this address can be used as a referrer. */
  isValid: boolean;
  refetch: () => void;
}

/**
 * Live-validates a candidate referrer wallet address against the DAO
 * contract. Only meaningful on a buyer's first-ever purchase — the contract
 * locks the referrer permanently after that (Users._createUnplacedUser), so
 * this hook should only be mounted while resolving a brand-new buyer.
 */
export const useReferrerFieldValidation = (
  rawAddress: string | undefined,
): UseReferrerFieldValidationReturn => {
  const { address: buyer } = useAccount();
  const candidate = (rawAddress ?? "").trim();

  const isWellFormed = isAddress(candidate);
  const isSelf =
    isWellFormed && !!buyer && candidate.toLowerCase() === buyer.toLowerCase();

  const referrer = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "getUser",
    args: [candidate as `0x${string}`],
    query: { enabled: isWellFormed && !isSelf },
  });

  const status = useMemo<ReferrerFieldStatus>(() => {
    if (candidate.length === 0) return "idle";
    if (!isWellFormed) return "invalid";
    if (isSelf) return "self";
    if (referrer.isLoading) return "checking";
    if (referrer.isError) return "unreachable";
    const user = referrer.data;
    if (!user) return "checking";
    if (!user.exists || (user.id !== ROOT_USER_ID && user.depth === 0n)) {
      return "not-member";
    }
    return "valid";
  }, [
    candidate,
    isWellFormed,
    isSelf,
    referrer.isLoading,
    referrer.isError,
    referrer.data,
  ]);

  return {
    status,
    isValid: status === "valid",
    refetch: referrer.refetch,
  };
};
